import { FC, useMemo } from 'react';
import { HStack, VStack } from '@chakra-ui/react';

import { TeamMembersRequestType } from '__generated__/GQL';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Text from 'components/_core/Text';
import PlaceholderLoader from 'components/PlaceholderLoader';
import RichText from 'components/RichText';

import { usePaymentCardRequestFees } from '../../hooks/usePaymentCardRequestFees';
import locales from './locales';

interface PaymentCardRequestFormFooterProps {
  isApprovalDisabled: boolean;
  isApprovalLoading: boolean;
  isRejectionDisabled: boolean;
  isRejectionLoading: boolean;
  onReject: () => void;
  type: TeamMembersRequestType;
}

const PaymentCardRequestFormFooter: FC<PaymentCardRequestFormFooterProps> = ({
  isApprovalDisabled,
  isApprovalLoading,
  isRejectionDisabled,
  isRejectionLoading,
  onReject,
  type,
}) => {
  const { fees, ...paymentCardRequestFeesQuery } =
    usePaymentCardRequestFees(type);

  const { hasEnoughFunds } = fees ?? {};

  const feesText = useMemo(() => {
    if (!fees) {
      return '';
    }

    const monthlyFeeWithouVat = fees.monthly.priceWithoutVAT;
    const orderFeeWithouVat = fees.order.priceWithoutVAT;

    if (monthlyFeeWithouVat && orderFeeWithouVat) {
      return locales.formatString(locales.fees.all, {
        monthlyFee: monthlyFeeWithouVat,
        orderFee: orderFeeWithouVat,
      }) as string;
    }

    if (monthlyFeeWithouVat) {
      return locales.formatString(locales.fees.onlyMonthly, {
        monthlyFee: monthlyFeeWithouVat,
      }) as string;
    }

    if (orderFeeWithouVat) {
      return locales.formatString(locales.fees.onlyOrder, {
        orderFee: orderFeeWithouVat,
      }) as string;
    }

    return locales.fees.free;
  }, [fees]);

  return (
    <>
      <VStack alignItems="start" spacing="space-8">
        <Text fontWeight="weight-500" variant="primary">
          {locales.fees.title}
        </Text>

        <PlaceholderLoader
          isLoaded={!paymentCardRequestFeesQuery.loading}
          minHeight={45}
          minWidth="100%"
        >
          <RichText
            renderStrong={(props) => (
              <Text
                {...props}
                display="inline"
                fontWeight="weight-500"
                variant="primary"
              />
            )}
          >
            {feesText}
          </RichText>
        </PlaceholderLoader>
      </VStack>

      {!paymentCardRequestFeesQuery.loading && !hasEnoughFunds ? (
        <Callout variant="error">
          <Callout.Content>{locales.errors.notEnoughFunds}</Callout.Content>
        </Callout>
      ) : null}

      <HStack paddingY="space-16" spacing="space-16">
        <Button
          flex={1}
          isDisabled={
            paymentCardRequestFeesQuery.loading || isRejectionDisabled
          }
          isLoading={isRejectionLoading}
          onClick={onReject}
          variant="secondary"
        >
          {locales.buttons.reject}
        </Button>

        <Button
          flex={1}
          isDisabled={
            paymentCardRequestFeesQuery.loading ||
            isApprovalDisabled ||
            !hasEnoughFunds
          }
          isLoading={isApprovalLoading}
          type="submit"
        >
          {locales.buttons.approve}
        </Button>
      </HStack>
    </>
  );
};

export default PaymentCardRequestFormFooter;
